import { Theme } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import React, { useRef, useState } from 'react'
import { chatPageSize, IChatMessage, ISystemMessage } from '../../../../api/types/chat'
import { useCableSubscription } from './hooks/useCableSubscription'
import { queryChatMessagesPage } from '../../../../api/queries'
import { useGlobalError } from '../../../../context/GlobalError'
import { useAuth } from '../../../../context/Auth'
import { ChatMessageInput } from './components/ChatMessageInput/ChatMessageInput'
import {
  characterQueryKey,
  gameCharsQueryKey,
  pendingActionsQueryKey,
} from '../../../../api/queries'
import { IGame } from '../../../../api/types/game'
import cx from 'classnames'
import { useQueryClient } from '@tanstack/react-query'
import { OnlineUsers } from './components/OnlineUsers/OnlineUsers'
import { ChatHistory } from './components/ChatHistory/ChatHistory'
import FaviconNotification from '../../../../faviconNotify'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexFlow: 'column',
      height: '100%',
      flex: '1 1 auto',
      overflow: 'auto',
      padding: '0px',
    },
    container: {
      display: 'flex',
      flexFlow: 'column',
      height: '100%',
    },
    content: {
      flex: '1 1 auto',
      overflow: 'auto',
      padding: '4px',
    },
  })
)

interface IMessageState {
  history: IChatMessage[]
  totalCount: number
}

interface IChatProps {
  game: IGame
}

export const Chat: React.FC<IChatProps> = ({ game }) => {
  const [messages, setMessages] = useState<IMessageState>()
  const [isLoading, setIsLoading] = useState(true)
  const { addError } = useGlobalError()
  const auth = useAuth()
  const classes = useStyles()
  const queryClient = useQueryClient()
  const chatRef = useRef<null | HTMLDivElement>(null)
  const [page, setPage] = useState(1)

  const fetchMessageHistory = () => {
    setIsLoading(true)
    queryChatMessagesPage(game.id, 1, chatPageSize)
      .then(data => {
        setMessages({ history: data.data, totalCount: data.totalCount })
      })
      .then(() => setPage(1))
      .then(() => setIsLoading(false))
      .catch(err => addError('Failed to fetch chat messages: ' + err?.message, err?.status))
  }

  const loadMore = () => {
    if (page >= 1 && messages && messages.history.length < messages.totalCount) {
      setIsLoading(true)
      return queryChatMessagesPage(game.id, page + 1, chatPageSize)
        .then(data =>
          setMessages(prevState => {
            return {
              history: [...data.data, ...(prevState?.history || [])],
              totalCount: data.totalCount,
            }
          })
        )
        .then(() => setPage(prevState => prevState + 1))
        .then(() => setIsLoading(false))
        .catch(err => addError('Failed to fetch chat messages: ' + err?.message, err?.status))
    }
    return Promise.reject()
  }

  const handleReceiveChatMessage = (data: IChatMessage) => {
    setMessages(prevState => {
      if (document.visibilityState !== 'visible') {
        FaviconNotification?.add()
      }
      return {
        history: [...(prevState?.history || []), data],
        totalCount: prevState?.totalCount || 0,
      }
    })
  }

  useCableSubscription('ApplicationCable::WhisperChatChannel', game.id, handleReceiveChatMessage)
  useCableSubscription('ApplicationCable::SystemChannel', game.id, (msg: ISystemMessage) => {
    if (msg.type === 'reload_history') {
      fetchMessageHistory()
    } else if (msg.type === 'reload_character') {
      queryClient.invalidateQueries(characterQueryKey(Number(msg.arg)))
      queryClient.invalidateQueries(gameCharsQueryKey(game.id))
    } else if (msg.type === 'new_pending_action') {
      queryClient.invalidateQueries(pendingActionsQueryKey(game.id))
    } else if (msg.type === 'join_game') {
      queryClient.invalidateQueries(gameCharsQueryKey(game.id))
    }
  })
  useCableSubscription(
    'ApplicationCable::GlobalChatChannel',
    game.id,
    handleReceiveChatMessage,
    () => {
      fetchMessageHistory()
    }
  )

  return (
    <>
      {auth?.username && (
        <div className={cx(classes.root)}>
          <OnlineUsers game={game} />
          <div className={classes.content}>
            <div className={classes.container}>
              <ChatHistory
                messages={messages?.history || []}
                chatRef={chatRef}
                isLoading={isLoading}
                loadMore={loadMore}
              />
              <ChatMessageInput game={game} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}
